import { Injectable } from '@angular/core';
import {
  DocumentData,
  QuerySnapshot,
  collection,
  doc,
  getDoc,
  getDocs,
  onSnapshot,
  orderBy,
  query,
  where,
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import {
  AppSession,
  CartOrder,
  EMPTY_MENU,
  MenuItem,
  MenuItemInput,
  OrderStatus,
  RestaurantMenu,
  RestaurantOrder,
  SectionLabel,
  TableCall,
  TableCallStatus,
} from './app.interface';
import { DataService } from './data.service';
import { FirebaseService } from './firebase.service';
import { mergeSectionLabels } from './menu-categories';

interface CallableResult {
  orderId?: string;
}

@Injectable({
  providedIn: 'root',
})
export class DbService {
  constructor(
    private dataService: DataService,
    private firebase: FirebaseService
  ) {}

  async readRestaurantMenu(restaurantId: string): Promise<RestaurantMenu> {
    const restaurantReference = doc(
      this.firebase.db,
      'restaurants',
      restaurantId
    );
    const [restaurantSnapshot, itemsSnapshot] = await Promise.all([
      getDoc(restaurantReference),
      getDocs(collection(restaurantReference, 'menuItems')),
    ]);
    if (!restaurantSnapshot.exists()) {
      throw new Error('Nema menija za željeni restoran.');
    }
    const items = itemsSnapshot.docs
      .map((item) => ({ ...item.data(), id: item.id } as MenuItem))
      .sort(
        (first, second) =>
          (first.sortOrder ?? 0) - (second.sortOrder ?? 0)
      );
    const menu = {
      ...EMPTY_MENU,
      ...restaurantSnapshot.data(),
      items,
    } as RestaurantMenu;
    menu.sectionLabels = mergeSectionLabels(menu.sectionLabels, items);
    this.dataService.setData(menu, restaurantId);
    return menu;
  }

  listenToMenuItems(
    restaurantId: string,
    next: (items: MenuItem[]) => void,
    error: (error: Error) => void
  ): () => void {
    const itemsQuery = query(
      collection(
        this.firebase.db,
        'restaurants',
        restaurantId,
        'menuItems'
      ),
      orderBy('sortOrder', 'asc')
    );
    return onSnapshot(
      itemsQuery,
      (snapshot) => {
        next(
          snapshot.docs.map(
            (item) => ({ ...item.data(), id: item.id } as MenuItem)
          )
        );
      },
      error
    );
  }

  listenToMenuCategories(
    restaurantId: string,
    next: (categories: SectionLabel[]) => void,
    error: (error: Error) => void
  ): () => void {
    return onSnapshot(
      doc(this.firebase.db, 'restaurants', restaurantId),
      (snapshot) => {
        const labels = snapshot.data()?.['sectionLabels'];
        next(
          Array.isArray(labels)
            ? labels.filter(
                (label): label is SectionLabel =>
                  typeof label?.['name'] === 'string' &&
                  typeof label?.['value'] === 'string'
              )
            : []
        );
      },
      error
    );
  }

  listenToTableOrders(
    session: AppSession,
    next: (orders: RestaurantOrder[], initialLoad: boolean) => void,
    error: (error: Error) => void
  ): () => void {
    if (!session.tableId) {
      throw new Error('Nedostaje oznaka stola.');
    }
    let initialLoad = true;
    const ordersQuery = query(
      collection(
        this.firebase.db,
        'restaurants',
        session.restaurantId,
        'orders'
      ),
      where('tableId', '==', session.tableId),
      where('active', '==', true),
      orderBy('createdAt', 'desc')
    );
    return onSnapshot(
      ordersQuery,
      (snapshot) => {
        next(this.mapOrders(snapshot), initialLoad);
        initialLoad = false;
      },
      error
    );
  }

  listenToRestaurantOrders(
    session: AppSession,
    next: (orders: RestaurantOrder[], newOrderIds: string[]) => void,
    error: (error: Error) => void
  ): () => void {
    let initialLoad = true;
    const ordersQuery = query(
      collection(
        this.firebase.db,
        'restaurants',
        session.restaurantId,
        'orders'
      ),
      where('active', '==', true),
      orderBy('createdAt', 'asc')
    );
    return onSnapshot(
      ordersQuery,
      (snapshot) => {
        const newOrderIds = initialLoad
          ? []
          : snapshot.docChanges()
              .filter((change) => change.type === 'added')
              .map((change) => change.doc.id);
        next(this.mapOrders(snapshot), newOrderIds);
        initialLoad = false;
      },
      error
    );
  }

  listenToTableCalls(
    session: AppSession,
    next: (calls: TableCall[], newCallIds: string[]) => void,
    error: (error: Error) => void
  ): () => void {
    let initialLoad = true;
    const callsQuery = query(
      collection(
        this.firebase.db,
        'restaurants',
        session.restaurantId,
        'tableCalls'
      ),
      where('status', 'in', ['requested', 'acknowledged']),
      orderBy('requestedAt', 'asc')
    );
    return onSnapshot(
      callsQuery,
      (snapshot) => {
        const newCallIds = initialLoad
          ? []
          : snapshot.docChanges()
              .filter(
                (change) =>
                  change.doc.data()['status'] === 'requested' &&
                  (change.type === 'added' || change.type === 'modified')
              )
              .map((change) => change.doc.id);
        const calls = snapshot.docs.map(
          (call) => ({ id: call.id, ...call.data() } as TableCall)
        );
        next(calls, newCallIds);
        initialLoad = false;
      },
      error
    );
  }

  async placeOrder(order: CartOrder): Promise<string> {
    const callable = httpsCallable<
      { items: { itemId: string; quantity: number }[]; note: string },
      CallableResult
    >(this.firebase.functions, 'placeOrder');
    const response = await callable({
      items: order.cart.map((item) => ({
        itemId: item.id,
        quantity: item.quantity,
      })),
      note: order.note,
    });
    if (!response.data.orderId) {
      throw new Error('Porudžbina nije sačuvana.');
    }
    return response.data.orderId;
  }

  async updateOrderStatus(orderId: string, status: OrderStatus): Promise<void> {
    const callable = httpsCallable<{ orderId: string; status: OrderStatus }>(
      this.firebase.functions,
      'updateOrderStatus'
    );
    await callable({ orderId, status });
  }

  async setOrderItemDelivered(
    orderId: string,
    itemId: string,
    delivered: boolean
  ): Promise<void> {
    const callable = httpsCallable<{
      orderId: string;
      itemId: string;
      delivered: boolean;
    }>(this.firebase.functions, 'setOrderItemDelivered');
    await callable({ orderId, itemId, delivered });
  }

  async callWaiter(): Promise<void> {
    const callable = httpsCallable(this.firebase.functions, 'callWaiter');
    await callable();
  }

  async setTableCallStatus(
    tableId: string,
    status: TableCallStatus
  ): Promise<void> {
    const callable = httpsCallable<{
      tableId: string;
      status: TableCallStatus;
    }>(this.firebase.functions, 'setTableCallStatus');
    await callable({ tableId, status });
  }

  async createMenuItem(item: MenuItemInput): Promise<string> {
    const callable = httpsCallable<MenuItemInput, { itemId: string }>(
      this.firebase.functions,
      'createMenuItem'
    );
    return (await callable(item)).data.itemId;
  }

  async updateMenuItem(itemId: string, item: MenuItemInput): Promise<void> {
    const callable = httpsCallable<MenuItemInput & { itemId: string }>(
      this.firebase.functions,
      'updateMenuItem'
    );
    await callable({ itemId, ...item });
  }

  async deleteMenuItem(itemId: string): Promise<void> {
    const callable = httpsCallable<{ itemId: string }>(
      this.firebase.functions,
      'deleteMenuItem'
    );
    await callable({ itemId });
  }

  async createMenuCategory(name: string): Promise<void> {
    const callable = httpsCallable<{ name: string }>(
      this.firebase.functions,
      'createMenuCategory'
    );
    await callable({ name });
  }

  private mapOrders(snapshot: QuerySnapshot<DocumentData>): RestaurantOrder[] {
    return snapshot.docs.map(
      (order) => ({ id: order.id, ...order.data() } as RestaurantOrder)
    );
  }

}
